import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/toast';
import { User, Github, AlertTriangle, CheckCircle, ExternalLink, Copy, ArrowLeft, Mail } from 'lucide-react';

/** アプリ内ブラウザ（WebView）を検出 */
function isInAppBrowser(): boolean {
  const ua = navigator.userAgent || '';
  return /Line\/|FBAN|FBAV|Instagram|Twitter|TwitterAndroid/i.test(ua);
}

export default function LoginPage() {
  const { login, register, guestLogin, startOAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const inApp = useMemo(() => isInAppBrowser(), []);

  // GTM: ログインページビュー
  useEffect(() => {
    window.dataLayer?.push({
      event: 'virtual_page_view',
      page_path: '/login',
      page_title: 'ログイン',
    });
  }, []);

  // OAuth コールバックエラーを検知（Supabase がエラー時に ?error= パラメータ付きでリダイレクトする）
  useEffect(() => {
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    if (error) {
      console.error('[Auth] OAuth callback error:', error, errorDescription);
      setOauthError(errorDescription || error);
    }
  }, [searchParams]);

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginEmail, loginPassword);
      navigate('/');
    } catch (err) {
      toast({ title: 'ログイン失敗', description: err instanceof Error ? err.message : 'エラーが発生しました', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (regPassword !== regPasswordConfirm) {
      toast({ title: '登録失敗', description: 'パスワードが一致しません', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      const result = await register(regEmail, regPassword, regName);
      if (result.needsEmailConfirmation) {
        setRegistrationSuccess(true);
      } else {
        navigate('/');
      }
    } catch (err) {
      toast({ title: '登録失敗', description: err instanceof Error ? err.message : 'エラーが発生しました', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await api.resetPasswordForEmail(resetEmail);
      setResetSent(true);
    } catch (err) {
      toast({ title: '送信失敗', description: err instanceof Error ? err.message : 'エラーが発生しました', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuest = async () => {
    setIsLoading(true);
    try {
      await guestLogin();
      navigate('/');
    } catch (err) {
      toast({ title: 'ゲストログイン失敗', description: err instanceof Error ? err.message : 'エラーが発生しました', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950 p-3 sm:p-4">
      <div className="w-full max-w-md space-y-4 sm:space-y-6">
        {/* Logo */}
        <div className="text-center space-y-2 sm:space-y-3">
          <img src="/logo.svg" alt="Hitorigoto" className="h-12 sm:h-16 mx-auto dark:hidden" />
          <img src="/logo-dark.svg" alt="Hitorigoto" className="h-12 sm:h-16 mx-auto hidden dark:block" />
          <p className="text-sm sm:text-base text-muted-foreground">声で記録する、あなただけの日記</p>
        </div>

        <Card className="rounded-xl shadow-sm">
          {/* OAuth icons row */}
          <CardContent className="pt-6 pb-4">
            {oauthError && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 mb-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                  <p className="text-sm font-medium text-destructive">ログインに失敗しました</p>
                </div>
                <p className="text-xs text-muted-foreground">{oauthError}</p>
              </div>
            )}

            {/* アプリ内ブラウザ警告 */}
            {inApp && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                  <p className="text-sm font-medium text-amber-800">
                    アプリ内ブラウザではGoogleログインが使えません
                  </p>
                </div>
                <p className="text-xs text-amber-700">
                  ブラウザ（Safari / Chrome）で開いてください
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                    onClick={() => {
                      // Android: intent URL で Chrome を起動
                      const url = window.location.href;
                      const intentUrl = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
                      window.location.href = intentUrl;
                      // iOS は intent が効かないので fallback としてコピー案内
                      setTimeout(() => {
                        navigator.clipboard.writeText(url);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }, 500);
                    }}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    ブラウザで開く
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    {copied ? 'コピーしました' : 'URLコピー'}
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {[
                { provider: 'google' as const, label: 'Googleで続ける', icon: (
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                )},
                { provider: 'twitter' as const, label: 'Xで続ける', icon: (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                )},
                { provider: 'github' as const, label: 'GitHubで続ける', icon: <Github className="h-5 w-5" /> },
              ].map(({ provider, label, icon }) => (
                <button
                  key={provider}
                  type="button"
                  onClick={() => startOAuth(provider)}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-3 h-11 rounded-xl border border-border bg-card text-sm font-medium transition-colors hover:bg-accent hover:border-primary/30 disabled:opacity-50"
                >
                  {icon}
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </CardContent>

          {/* Divider */}
          <div className="relative px-6">
            <div className="absolute inset-0 flex items-center px-6">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-2 text-muted-foreground">または</span>
            </div>
          </div>

          <Tabs defaultValue="login">
            <CardHeader className="pb-3 pt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">ログイン</TabsTrigger>
                <TabsTrigger value="register">新規登録</TabsTrigger>
              </TabsList>
            </CardHeader>

            <TabsContent value="login">
              {showResetForm ? (
                resetSent ? (
                  <CardContent className="py-6">
                    <div className="text-center space-y-3">
                      <Mail className="h-12 w-12 text-primary mx-auto" />
                      <h3 className="text-lg font-semibold">メールを送信しました</h3>
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">{resetEmail}</span> にパスワードリセット用のリンクを送信しました。
                        メール内のリンクをクリックして、新しいパスワードを設定してください。
                      </p>
                      <p className="text-xs text-muted-foreground">
                        メールが届かない場合は、迷惑メールフォルダもご確認ください。
                      </p>
                      <Button
                        variant="ghost"
                        className="mt-2"
                        onClick={() => { setShowResetForm(false); setResetSent(false); setResetEmail(''); }}
                      >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        ログインに戻る
                      </Button>
                    </div>
                  </CardContent>
                ) : (
                  <form onSubmit={handleResetPassword}>
                    <CardContent className="space-y-4">
                      <div className="space-y-1">
                        <button
                          type="button"
                          className="flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => setShowResetForm(false)}
                        >
                          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                          ログインに戻る
                        </button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        登録済みのメールアドレスを入力してください。パスワードリセット用のリンクをお送りします。
                      </p>
                      <div className="space-y-2">
                        <label className="text-sm font-medium" htmlFor="reset-email">メールアドレス</label>
                        <Input
                          id="reset-email"
                          type="email"
                          placeholder="mail@example.com"
                          value={resetEmail}
                          onChange={e => setResetEmail(e.target.value)}
                          required
                        />
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button type="submit" className="w-full" disabled={isLoading}>
                        {isLoading ? '送信中...' : 'リセットメールを送信'}
                      </Button>
                    </CardFooter>
                  </form>
                )
              ) : (
                <form onSubmit={handleLogin}>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="login-email">メールアドレス</label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="mail@example.com"
                        value={loginEmail}
                        onChange={e => setLoginEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium" htmlFor="login-password">パスワード</label>
                        <button
                          type="button"
                          className="text-xs text-muted-foreground hover:text-primary transition-colors"
                          onClick={() => setShowResetForm(true)}
                        >
                          パスワードを忘れた方
                        </button>
                      </div>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="********"
                        value={loginPassword}
                        onChange={e => setLoginPassword(e.target.value)}
                        required
                      />
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? 'ログイン中...' : 'ログイン'}
                    </Button>
                  </CardFooter>
                </form>
              )}
            </TabsContent>

            <TabsContent value="register">
              {registrationSuccess ? (
                <CardContent className="py-6">
                  <div className="text-center space-y-3">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      className="inline-flex"
                    >
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                    </motion.div>
                    <h3 className="text-lg font-semibold">確認メールを送信しました</h3>
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">{regEmail}</span> に確認メールを送信しました。
                      メール内のリンクをクリックして、アカウントを有効にしてください。
                    </p>
                    <p className="text-xs text-muted-foreground">
                      メールが届かない場合は、迷惑メールフォルダもご確認ください。
                    </p>
                  </div>
                </CardContent>
              ) : (
                <form onSubmit={handleRegister}>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="reg-name">ニックネーム</label>
                      <Input
                        id="reg-name"
                        type="text"
                        placeholder="たろう"
                        value={regName}
                        onChange={e => setRegName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="reg-email">メールアドレス</label>
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="mail@example.com"
                        value={regEmail}
                        onChange={e => setRegEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="reg-password">パスワード</label>
                      <Input
                        id="reg-password"
                        type="password"
                        placeholder="8文字以上"
                        value={regPassword}
                        onChange={e => setRegPassword(e.target.value)}
                        required
                        minLength={8}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="reg-password-confirm">パスワード（確認）</label>
                      <Input
                        id="reg-password-confirm"
                        type="password"
                        placeholder="もう一度入力"
                        value={regPasswordConfirm}
                        onChange={e => setRegPasswordConfirm(e.target.value)}
                        required
                        minLength={8}
                      />
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? '登録中...' : 'アカウント作成'}
                    </Button>
                  </CardFooter>
                </form>
              )}
            </TabsContent>
          </Tabs>
        </Card>

        {/* Guest login */}
        <Button variant="ghost" className="w-full text-muted-foreground" onClick={handleGuest} disabled={isLoading}>
          <User className="h-4 w-4 mr-2" />
          ゲストとして試す
        </Button>

        <p className="text-center text-xs text-muted-foreground mt-2">
          利用開始により
          <a href="/terms" className="underline hover:text-foreground mx-0.5">利用規約</a>
          および
          <a href="/privacy" className="underline hover:text-foreground mx-0.5">プライバシーポリシー</a>
          に同意したものとみなします。
        </p>
      </div>
    </div>
  );
}
